import React, { useEffect, useState } from 'react';
import { fetchProfile, getCompany } from '../../../utils/ktcUtils';
import { getToken } from '../../../utils/TokenUtils';
import { API_URL } from '../../../constants/api';
import '../../../css/invoiceBook.css';

const tableConfig = {
  Invoice: {
    title: 'Invoice Book',
    description:
      'A detailed record of all invoices issued to customers, including client information, services provided, and payable amounts.',
    columns: ['No', 'Name', 'Tell', 'Description', 'Amount', 'Username'],
  },

  Receipt: {
    title: 'Receipt Book',
    description:
      'A comprehensive log of all payments received from customers, showing payer details, payment purpose, and received amounts.',
    columns: ['No', 'Name', 'Tell', 'Description', 'Amount', 'Username'],
  },

  InvoiceReceipt: {
    title: 'Invoice & Receipt Book',
    description:
      'A combined record tracking issued invoices and corresponding payments, helping to monitor outstanding balances and cleared transactions.',
    columns: [
      'No',
      'ID/Name/Tell',
      'Description',
      'Invoice',
      'Paid',
      'Username',
    ],
    total: ['No', 'Date', 'Invoice', 'Paid'],
  },

  Expense: {
    title: 'Expense Book',
    description:
      'A full expense-tracking register covering company spending, categorized by expense type, purpose, and amount spent.',
    columns: ['No', 'Expense Type', 'Description', 'Amount', 'Username'],
  },
};

const generalDescription =
  'This bookkeeping system is fully integrated with the KAAFI PAY platform, allowing smooth data transmission and real-time synchronization. Business owners, employees, and customers can easily track financial records, monitor reports, and access updated information anytime.';

const moneyKeys = ['amount', 'cost', 'paid'];

const renderTotals = (columns) => {
  // Filter the columns that match moneyKeys
  const matched = columns.filter((col) =>
    moneyKeys.includes(col.toLowerCase())
  );

  if (matched.length === 0) return null;

  return (
    <div
      className="totals-container"
      style={{ display: 'flex', gap: '20px', marginTop: '0px' }}
    >
      {matched.map((col) => (
        <div key={col} className={`total-${col.toLowerCase()}`}>
          Total {col}: ____________________
        </div>
      ))}
    </div>
  );
};
const InvoiceBook = () => {
  const [business, setBusiness] = useState(null);
  const [company, setCompany] = useState(null);
  const [type, setType] = useState(null);

  const token = getToken();

  useEffect(() => {
    fetchProfile(token, 'user', setBusiness); // business info from server
    getCompany(setCompany); // company branding from server
  }, [token]);

  const selected = type && tableConfig[type] ? tableConfig[type] : null;

  if (!selected) {
    return (
      <div style={{ textAlign: 'center', padding: '40px' }}>
        <h2>Select Invoice Type</h2>
        {Object.keys(tableConfig).map((key) => (
          <button
            key={key}
            onClick={() => setType(key)}
            style={{
              padding: '10px 20px',
              margin: '8px',
              border: '1px solid #000',
              cursor: 'pointer',
            }}
          >
            {tableConfig[key].title}
          </button>
        ))}
      </div>
    );
  }

  if (!business || !company) return <p>Loading...</p>;

  const generateRows = (columns, rows) => {
    return Array.from({ length: rows }, (_, rowIndex) => (
      <tr key={rowIndex}>
        {columns.map((col, colIndex) => (
          <td
            key={colIndex}
            className={col
              .toLowerCase()
              .replace(/\s+/g, '-')
              .replaceAll('/', '-')}
          >
            {colIndex === 0 ? rowIndex + 1 : ''}
          </td>
        ))}
      </tr>
    ));
  };

  return (
    <div className="row">
      {/* ============ HEADER ============ */}
      <div className="col-12">
        <div className="invoice-header">
          <img
            src={`${API_URL}/${company.logo}`}
            alt="Business Logo"
            className="invoice-logo"
          />

          <div className="container row">
            <div className="col-12">
              <h3>
                KAAFI PAY - {selected.title} &nbsp; &nbsp; &nbsp; &nbsp; -
                &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp;
                <small>Date: ____ / ____ / ________</small>
              </h3>
            </div>
            {/* 
            <div className="col-6">
              <h2>{business['Business Name']}</h2>
              <p>Business ID: {business.businessID} - Powered by KTC</p>
            </div> */}
            {/* <div className="12">
              <p>{selected.description} </p>
            </div> */}
          </div>
        </div>

        {/* ============ TABLE ============ */}
        <table className="invoice-table">
          <thead>
            <tr>
              {selected.columns.map((col, index) => (
                <th
                  key={index}
                  className={col
                    .toLowerCase()
                    .replace(/\s+/g, '-')
                    .replaceAll('/', '-')}
                >
                  {col}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>{generateRows(selected.columns, 25)}</tbody>

          {/* ============ FOOTER ============ */}
          <tfoot>
            {/* <tr>
            <td colSpan={selected.columns.length}>
              <div className="container row">
                <div className="col-3">Name: ________________________</div>
                <div className="col-3">Signature: ____________________</div>
                <div className="col-6">
                  {/* Render totals below the table or above footer */}
            {/* {renderTotals(selected.columns)}
                </div>
              </div>
            </td>
          </tr> */}

            <tr>
              {selected.columns.map((col, index) => {
                const colKey = col.toLowerCase();

                const isTotalCol =
                  colKey.includes('amount') ||
                  colKey.includes('cost') ||
                  colKey.includes('paid') ||
                  colKey.includes('invoice');

                // First numeric column index
                const firstTotalIndex = selected.columns.findIndex((c) =>
                  ['amount', 'cost', 'paid', 'invoice'].some((key) =>
                    c.toLowerCase().includes(key)
                  )
                );

                // If this is the column BEFORE the first numeric column → TOTAL label
                if (index === firstTotalIndex - 1) {
                  return (
                    <td key={index} className="footer-total-label">
                      <strong>Total</strong>
                    </td>
                  );
                }

                // Numeric columns → blank total line
                if (isTotalCol) {
                  return (
                    <td key={index} className="footer-total-value">
                      ____________________
                    </td>
                  );
                }

                // Second last column → Name
                if (index === selected.columns.length - 2) {
                  return (
                    <td key={index} className="footer-name">
                      Name: ____________________
                    </td>
                  );
                }

                // Last column → Signature
                if (index === selected.columns.length - 1) {
                  return (
                    <td key={index} className="footer-signature">
                      Signature: ____________________
                    </td>
                  );
                }

                // All other cells stay empty (required to keep alignment)
                return <td key={index}></td>;
              })}
            </tr>
          </tfoot>
        </table>

        <table className="invoice-table">
          <thead>
            <tr>
              {selected.total.map((col, index) => (
                <th
                  key={index}
                  className={col
                    .toLowerCase()
                    .replace(/\s+/g, '-')
                    .replaceAll('/', '-')}
                >
                  {col}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>{generateRows(selected.total, 30)}</tbody>

          {/* ============ FOOTER ============ */}
          <tfoot>
            {/* <tr>
            <td colSpan={selected.columns.length}>
              <div className="container row">
                <div className="col-3">Name: ________________________</div>
                <div className="col-3">Signature: ____________________</div>
                <div className="col-6">
                  {/* Render totals below the table or above footer */}
            {/* {renderTotals(selected.columns)}
                </div>
              </div>
            </td>
          </tr> */}

            <tr>
              {selected.total.map((col, index) => {
                const colKey = col.toLowerCase();

                const isTotalCol =
                  colKey.includes('amount') ||
                  colKey.includes('cost') ||
                  colKey.includes('paid') ||
                  colKey.includes('invoice');
                console.log(col, ':', isTotalCol);
                // First numeric column index
                const firstTotalIndex = selected.total.findIndex((c) =>
                  ['amount', 'cost', 'paid', 'invoice'].some((key) =>
                    c.toLowerCase().includes(key)
                  )
                );

                // If this is the column BEFORE the first numeric column → TOTAL label
                if (index === firstTotalIndex - 1) {
                  return (
                    <td
                      key={index}
                      className="footer-total-label"
                      style={{ textAlign: 'right' }}
                    >
                      <strong>Total</strong>
                    </td>
                  );
                }

                // Numeric columns → blank total line
                if (isTotalCol) {
                  return (
                    <td key={index} className="footer-total-value">
                      ____________________
                    </td>
                  );
                }

                // Second last column → Name
                if (index === selected.columns.length - 2) {
                  return (
                    <td key={index} className="footer-name">
                      Name: ____________________
                    </td>
                  );
                }

                // Last column → Signature
                if (index === selected.columns.length - 1) {
                  return (
                    <td key={index} className="footer-signature">
                      Signature: ____________________
                    </td>
                  );
                }

                // All other cells stay empty (required to keep alignment)
                return <td key={index}></td>;
              })}
            </tr>
          </tfoot>
        </table>
        {/* <p
          style={{
            marginTop: '5px',
            border: '1px solid black',
            padding: '5px',
            textAlign: 'center',
          }}
        >
          N.B: {generalDescription}
        </p> */}
      </div>
    </div>
  );
};

export default InvoiceBook;
